/* ─── NoCosign: optional fit fields (height + weight) in the cart ───

   Progressive enhancement over snippets/nocosign-fit-fields.liquid. Liquid
   already renders the inputs pre-filled from cart.attributes; this element
   adds the unit toggle and writes changes back to the cart.

   Two things shape the design:

     1. assets/cart.js re-renders the cart by replacing innerHTML wholesale —
        `#CartDrawer-Body` for the drawer, `#shopify-section-<id> cart-items`
        for the cart page — on every quantity change and every remove. It
        publishes no pubsub event and fires no callback we could hook. So the
        fields are destroyed and rebuilt constantly, and a custom element is
        the only thing that reliably survives it: assigning the section HTML
        to a live element upgrades <fit-fields> again, and connectedCallback
        re-wires from scratch. Nothing is bound to a node that outlives a
        re-render.

     2. The re-rendered HTML comes back from the section-rendering API, which
        reads cart.attributes server-side, so the rebuilt inputs come back
        correct on their own — as long as the value reached the cart first.
        The window where it has not is covered by the module-level state
        below, which outlives every element instance: whatever the shopper
        last typed wins over whatever the section render happened to carry.

   The snippet renders inside the theme's cart <form>, so Enter is swallowed
   here — implicit submission would fire the checkout button next to it. */

if (!customElements.get('fit-fields')) {
  const UNITS_KEY = 'nocosign:fit-units';
  const DEBOUNCE_MS = 500;
  const LB_PER_KG = 2.2046226218;
  const CM_PER_IN = 2.54;

  /* Every live instance — the cart page renders one, and the drawer that sits
     over it renders another. They mirror each other. */
  const instances = new Set();

  /* Module level on purpose: this is what survives a section re-render. */
  const state = { units: 'metric', height: '', weight: '', seeded: false };
  let saved = { height: null, weight: null };
  let timer = null;
  let pageHideBound = false;

  const readUnits = () => {
    try {
      const value = localStorage.getItem(UNITS_KEY);
      return value === 'imperial' || value === 'metric' ? value : null;
    } catch (error) {
      /* Private mode, blocked storage — the toggle just stops being sticky. */
      return null;
    }
  };

  const writeUnits = (value) => {
    try {
      localStorage.setItem(UNITS_KEY, value);
    } catch (error) {
      /* Ignore. */
    }
  };

  const cartUpdateUrl = () => {
    const base = (window.routes && window.routes.cart_update_url) || '/cart/update';
    return `${base}.js`;
  };

  const payload = () =>
    JSON.stringify({ attributes: { Height: state.height, Weight: state.weight } });

  const unsaved = () => saved.height !== state.height || saved.weight !== state.weight;

  const commit = () => {
    if (!unsaved()) return;

    const sending = { height: state.height, weight: state.weight };
    const body = payload();

    fetch(cartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
        saved = sending;
      })
      .catch(() => {
        /* Leave `saved` alone so the next blur, or the pagehide beacon, retries.
           This field is optional; a failure must never surface as an error the
           shopper has to clear before checking out. */
      });
  };

  const queue = () => {
    clearTimeout(timer);
    timer = setTimeout(commit, DEBOUNCE_MS);
  };

  const flush = () => {
    clearTimeout(timer);
    commit();
  };

  /* Last resort. Clicking Checkout blurs the input, which flushes, but the form
     submit can navigate away before that fetch lands. sendBeacon survives it. */
  const beacon = () => {
    if (!unsaved()) return;
    clearTimeout(timer);
    try {
      const blob = new Blob([payload()], { type: 'application/json' });
      if (navigator.sendBeacon && navigator.sendBeacon(cartUpdateUrl(), blob)) {
        saved = { height: state.height, weight: state.weight };
        return;
      }
    } catch (error) {
      /* Fall through. */
    }
    commit();
  };

  /* ── value parsing and formatting ────────────────────────────────── */

  const parseHeight = (raw) => {
    const value = (raw || '').trim();
    let match = value.match(/^(\d+(?:\.\d+)?)\s*cm$/i);
    if (match) return { units: 'metric', cm: Number(match[1]) };
    match = value.match(/^(\d+)\s*'\s*(\d+(?:\.\d+)?)?\s*"?$/);
    if (match) return { units: 'imperial', ft: Number(match[1]), in: Number(match[2] || 0) };
    return null;
  };

  const parseWeight = (raw) => {
    const value = (raw || '').trim();
    let match = value.match(/^(\d+(?:\.\d+)?)\s*kg$/i);
    if (match) return { units: 'metric', kg: Number(match[1]) };
    match = value.match(/^(\d+(?:\.\d+)?)\s*lbs?$/i);
    if (match) return { units: 'imperial', lb: Number(match[1]) };
    return null;
  };

  /* Always a unit-bearing string, never a bare number: this is the label text
     a NoCosign staffer reads on the order in admin, with no toggle for context. */
  const formatHeight = (parsed) => {
    if (!parsed) return '';
    if (parsed.units === 'metric') return `${Math.round(parsed.cm)} cm`;
    return `${Math.round(parsed.ft)}'${Math.round(parsed.in)}"`;
  };

  const formatWeight = (parsed) => {
    if (!parsed) return '';
    if (parsed.units === 'metric') return `${Math.round(parsed.kg)} kg`;
    return `${Math.round(parsed.lb)} lbs`;
  };

  const convertHeight = (parsed, units) => {
    if (!parsed || parsed.units === units) return parsed;
    if (units === 'imperial') {
      const total = Math.round(parsed.cm / CM_PER_IN);
      let ft = Math.floor(total / 12);
      let inches = total - ft * 12;
      if (inches === 12) {
        ft += 1;
        inches = 0;
      }
      return { units: 'imperial', ft, in: inches };
    }
    return { units: 'metric', cm: Math.round((parsed.ft * 12 + parsed.in) * CM_PER_IN) };
  };

  const convertWeight = (parsed, units) => {
    if (!parsed || parsed.units === units) return parsed;
    if (units === 'imperial') return { units: 'imperial', lb: Math.round(parsed.kg * LB_PER_KG) };
    return { units: 'metric', kg: Math.round(parsed.lb / LB_PER_KG) };
  };

  /* ── element ──────────────────────────────────────────────── */

  class FitFields extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;

      this.groups = Array.from(this.querySelectorAll('[data-fit-group]'));
      this.buttons = Array.from(this.querySelectorAll('[data-fit-unit]'));
      this.inputs = {};
      Array.from(this.querySelectorAll('[data-fit-input]')).forEach((input) => {
        this.inputs[input.dataset.fitInput] = input;
      });

      /* Never assume the markup. */
      if (!this.groups.length || !this.buttons.length || !this.inputs['height-cm']) return;

      this.initialised = true;

      /* First instance of the page-load seeds module state from what Liquid
         rendered; later instances (a re-render, or the second cart surface)
         must not, or an in-flight edit would be thrown away. */
      if (!state.seeded) {
        state.seeded = true;
        state.height = this.dataset.height || '';
        state.weight = this.dataset.weight || '';
        saved = { height: state.height, weight: state.weight };
        /* A saved value's own unit beats the remembered preference — it is
           what this shopper actually typed. Otherwise localStorage, then
           metric. No geolocation, no other inference. */
        state.units = state.height || state.weight
          ? this.dataset.units || 'metric'
          : readUnits() || 'metric';
      }

      this.onInput = this.onInput.bind(this);
      this.onBlur = this.onBlur.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onUnitClick = this.onUnitClick.bind(this);

      this.addEventListener('input', this.onInput);
      this.addEventListener('focusout', this.onBlur);
      this.addEventListener('keydown', this.onKeydown);
      this.buttons.forEach((button) => button.addEventListener('click', this.onUnitClick));

      instances.add(this);

      if (!pageHideBound) {
        pageHideBound = true;
        window.addEventListener('pagehide', beacon);
      }

      this.render();
    }

    disconnectedCallback() {
      if (!this.initialised) return;
      instances.delete(this);
      this.initialised = false;
      /* cart.js is about to swap this whole subtree out. Anything typed and
         not yet debounced would go with it. */
      flush();
    }

    /* Paint module state into this instance. Never touches the field the
       shopper is currently in — that would eat the caret mid-word. */
    render() {
      const height = parseHeight(state.height);
      const weight = parseWeight(state.weight);
      const heightIn = convertHeight(height, state.units);
      const weightIn = convertWeight(weight, state.units);

      this.set('height-cm', heightIn && heightIn.units === 'metric' ? Math.round(heightIn.cm) : '');
      this.set('height-ft', heightIn && heightIn.units === 'imperial' ? Math.round(heightIn.ft) : '');
      this.set('height-in', heightIn && heightIn.units === 'imperial' ? Math.round(heightIn.in) : '');
      this.set('weight-kg', weightIn && weightIn.units === 'metric' ? Math.round(weightIn.kg) : '');
      this.set('weight-lb', weightIn && weightIn.units === 'imperial' ? Math.round(weightIn.lb) : '');

      this.groups.forEach((group) => {
        group.hidden = group.dataset.fitGroup !== state.units;
      });

      this.buttons.forEach((button) => {
        const on = button.dataset.fitUnit === state.units;
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    set(key, value) {
      const input = this.inputs[key];
      if (!input || input === document.activeElement) return;
      input.value = value === '' ? '' : String(value);
    }

    /* Digits only, then clamped to the field's own range. Done on read rather
       than by rewriting input.value, so nothing fights the shopper mid-type. */
    number(key) {
      const input = this.inputs[key];
      if (!input) return null;
      const digits = input.value.replace(/[^\d]/g, '');
      if (!digits) return null;
      const min = Number(input.dataset.fitMin);
      const max = Number(input.dataset.fitMax);
      return Math.min(max, Math.max(min, Number(digits)));
    }

    /* Read this instance's visible inputs into the two stored strings. */
    collect() {
      if (state.units === 'metric') {
        const cm = this.number('height-cm');
        const kg = this.number('weight-kg');
        state.height = formatHeight(cm === null ? null : { units: 'metric', cm });
        state.weight = formatWeight(kg === null ? null : { units: 'metric', kg });
      } else {
        const ft = this.number('height-ft');
        const inches = this.number('height-in');
        const lb = this.number('weight-lb');
        /* Inches alone is not a height. Feet alone is. */
        state.height = formatHeight(ft === null ? null : { units: 'imperial', ft, in: inches || 0 });
        state.weight = formatWeight(lb === null ? null : { units: 'imperial', lb });
      }
    }

    mirror() {
      instances.forEach((instance) => {
        if (instance !== this) instance.render();
      });
    }

    onInput(event) {
      if (!event.target.dataset || !event.target.dataset.fitInput) return;
      this.collect();
      this.mirror();
      queue();
    }

    onBlur(event) {
      if (!event.target.dataset || !event.target.dataset.fitInput) return;
      this.collect();
      /* Re-paint so an out-of-range or half-typed entry settles to what was
         actually stored. */
      this.render();
      this.mirror();
      flush();
    }

    onKeydown(event) {
      if (event.key !== 'Enter') return;
      if (!event.target.dataset || !event.target.dataset.fitInput) return;
      /* Implicit submission would press the checkout button sitting under
         these fields. */
      event.preventDefault();
      event.target.blur();
    }

    onUnitClick(event) {
      const next = event.currentTarget.dataset.fitUnit;
      if (!next || next === state.units) return;

      /* Take what is on screen before switching, then re-express it in the
         new units so nothing typed is lost. */
      this.collect();
      const height = convertHeight(parseHeight(state.height), next);
      const weight = convertWeight(parseWeight(state.weight), next);

      state.units = next;
      state.height = formatHeight(height);
      state.weight = formatWeight(weight);
      writeUnits(next);

      this.render();
      this.mirror();
      flush();
    }
  }

  customElements.define('fit-fields', FitFields);
}
